import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DoodStripboard } from "components/dashboard/body/projects/panels/schedule/types";
import { SceneReport } from "components/dashboard/body/projects/panels/script/types";
import { USER_API } from "config";
import { useAxios } from "hooks/useAxios";
import {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import {
  BlockDataType,
  GetSceneResponse,
  NoteType,
  SceneData,
  TagDefinitionType,
} from "store/types/project";
import { useProjectsApi } from "hooks/useProjects";
import { scriptKeys } from "queries";
import { useParams } from "react-router-dom";
import { mergedTagClusterDisplayLabel } from "features/script-editor/lib/merged-tag-provenance";
import { hydrateProjectFromDetailCache } from "utils/project/hydrateProjectFromDetailCache";
import { resolveScreenplayDocumentId } from "utils/project/resolveScreenplayDocumentId";

interface Props {
  children: React.ReactNode;
}

export type ScriptContextType = {
  scene: SceneData["scene"];
  scenes: [];
  /** True while `scenes` still holds the previous draft's list during a draft switch. */
  scenesArePrevious: boolean;
  sceneId: number | undefined;
  setSceneId: (sceneId: number | undefined) => void;
  documentId: string | undefined;
  tagDefinitions: SceneData["tag_definitions"];
  tagCategories: SceneData["tag_categories"];
  notesList: NoteType[];
  setNotesList: Dispatch<SetStateAction<NoteType[]>>;
  selectedTagDef: number | undefined;
  setSelectedTagDef: Dispatch<SetStateAction<number | undefined>>;
  hoveredTagDef: number | undefined;
  setHoveredTagDef: Dispatch<SetStateAction<number | undefined>>;
  multiSelectedTagDef: number[];
  setMultiSelectedTagDef: Dispatch<SetStateAction<number[]>>;
  isSceneLoading: boolean;
  scenesLoaded: boolean;
  /** The current draft's scene list never loaded and its last request failed. */
  scenesLoadFailed: boolean;
  /** True while the scene list request runs, including a retry after a failure. */
  isFetchingScenes: boolean;
  selectedBlock: BlockDataType | undefined;
  setSelectedBlock: Dispatch<SetStateAction<BlockDataType | undefined>>;
  doodFilteredStripboard: DoodStripboard | undefined;
  setDoodFilteredStripboard: Dispatch<SetStateAction<DoodStripboard | undefined>>;
  sceneReport: SceneReport[];
  setSceneReport: Dispatch<SetStateAction<SceneReport[]>>;
  castMemberTagId: number | undefined;
  setCastMemberTagId: Dispatch<SetStateAction<number | undefined>>;
  refetchScene: () => void;
  refetchScenes: () => void;
  sceneHeading: string;
};

export const ScriptContext = createContext<ScriptContextType>({} as ScriptContextType);

export const ScriptContextProvider = ({ children }: Props) => {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { projectId: paramProjectId } = useParams();

  const { useProject } = useProjectsApi({});
  const validId = !!paramProjectId && paramProjectId !== "0";
  const { data: project } = useProject(validId ? paramProjectId : undefined);

  const effectiveProject = useMemo(
    () =>
      hydrateProjectFromDetailCache(queryClient, validId ? String(paramProjectId) : "", project),
    [queryClient, validId, paramProjectId, project]
  );

  const projectId = String(effectiveProject?.projectId ?? paramProjectId ?? "");
  const documentId = useMemo(() => {
    if (!validId || !projectId) {
      return undefined;
    }
    const resolved = resolveScreenplayDocumentId({
      queryClient,
      projectId,
      project: effectiveProject,
    });
    return resolved || undefined;
  }, [queryClient, validId, projectId, effectiveProject]);

  // The picked scene is bound to the draft it was picked in.
  const [scenePick, setScenePick] = useState<{ documentId: string; sceneId: number }>();

  const [selectedTagDef, setSelectedTagDef] = useState<number>();
  const [hoveredTagDef, setHoveredTagDef] = useState<number>();
  const [multiSelectedTagDef, setMultiSelectedTagDef] = useState<number[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<BlockDataType | undefined>();

  // DooD
  const [doodFilteredStripboard, setDoodFilteredStripboard] = useState<
    DoodStripboard | undefined
  >();

  // Report
  const [sceneReport, setSceneReport] = useState<SceneReport[]>([]);

  // Tech debt. We dont have stable IDs in a database after it was partially wiped.
  const [castMemberTagId, setCastMemberTagId] = useState<number>();

  const [notesList, setNotesList] = useState<NoteType[]>([
    {
      created_at: "",
      note_id: undefined,
      scene_id: undefined,
      updated_at: "",
      value: "",
    },
  ]);

  const {
    data: scenes,
    refetch: refetchScenes,
    isPreviousData: scenesArePrevious,
    isError: isScenesError,
    isFetching: isFetchingScenes,
  } = useQuery({
    enabled: true,
    queryKey: scriptKeys.scenes((projectId || 0) + "", (documentId || 0) + ""),
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true,
    // Poll until the open draft's own list has scenes. `query.state.data` is this draft's
    // answer, never the previous draft's rows shown meanwhile.
    refetchInterval: (_data, query) => {
      const hasScenes = Array.isArray(query.state.data) && query.state.data.length > 0;
      if ((query.state.status === "error" && query.state.errorUpdateCount >= 3) || hasScenes) {
        return false;
      }
      return 10000;
    },
    queryFn: async () => {
      if (!documentId || !projectId || projectId === "0" || documentId === "0") {
        return [];
      }

      const pid = projectId.toString();
      const did = documentId.toString();

      // A failed request stays an error, so a script whose scenes did not load never reads as
      // a script with no scenes.
      const { data } = await axios.get(USER_API.getAllScenes(pid, did));
      return data && data.length > 0 ? data : [];
    },
  });

  const scenesLoaded = !scenesArePrevious && Array.isArray(scenes) && scenes.length > 0;
  // The last request failed and left no scenes to show. A failed refresh keeps the scenes already
  // shown; a cached empty list does not, since it cannot tell "no scenes" from "couldn't load".
  const scenesLoadFailed = isScenesError && !scenes?.length;

  // Consumers only ever see a scene of the current draft's own list. Right after a draft
  // switch, and while the previous draft's rows are shown, there is no scene.
  const sceneId =
    scenePick &&
    scenePick.documentId === documentId &&
    !scenesArePrevious &&
    scenes?.some((scene: any) => scene.scene_id === scenePick.sceneId)
      ? scenePick.sceneId
      : undefined;
  const setSceneId = useCallback(
    (nextSceneId: number | undefined) => {
      setScenePick(
        documentId && nextSceneId !== undefined ? { documentId, sceneId: nextSceneId } : undefined
      );
    },
    [documentId]
  );

  const {
    refetch: refetchScene,
    data: sceneData,
    isLoading: isSceneLoading,
  } = useQuery<SceneData>({
    enabled:
      !!projectId &&
      !!documentId &&
      !!sceneId &&
      // Only enable if the sceneId exists in the current draft's scenes. The previous draft's
      // scenes stay visible while the new draft loads, so they must not count.
      !scenesArePrevious &&
      scenes &&
      scenes.length > 0 &&
      scenes.some((scene: any) => scene.scene_id === sceneId),
    queryKey: scriptKeys.scene(projectId + "", documentId + "", sceneId + ""),
    queryFn: async () => {
      if (!documentId || !projectId || !sceneId) return [];

      const pid = projectId.toString();
      const did = documentId.toString();
      const sid = sceneId.toString();

      const { data } = await axios.get(USER_API.getScene(pid, did, sid));

      return { ...data, tag_definitions: groupTagsByMergedLabel(data.tag_definitions) };
    },
  });

  // Refetch queries when documentId changes (but only if it's actually different)
  useEffect(() => {
    if (documentId) {
      // Reset scene to undefined when document changes to force selection of first scene
      setSceneId(undefined);
      refetchScenes();
    }
  }, [documentId, refetchScenes]);

  useEffect(() => {
    if (projectId && documentId && (!scenes || scenes.length === 0)) {
      refetchScenes();
    }
  }, [projectId, documentId, scenes, refetchScenes]);

  // Keep a scene selected: pick the first one whenever none is selected or the selected one
  // is not in this draft. Runs on render state, not inside the query, so it never acts on a
  // stale selection. Skips the previous draft's scenes shown while the new draft loads.
  useEffect(() => {
    if (!scenes || scenes.length === 0 || scenesArePrevious) return;
    if (!sceneId || !scenes.some((scene: any) => scene.scene_id === sceneId)) {
      setSceneId((scenes[0] as any).scene_id);
    }
  }, [scenes, sceneId, scenesArePrevious, setSceneId]);

  // The scene list already carries each heading, so a picked scene is named at once instead of
  // showing nothing until its details load. The details win once they arrive.
  const listedSceneHeading = (
    scenes as Array<{ scene_id: number; scene_heading?: string }> | undefined
  )?.find((scene) => scene.scene_id === sceneId)?.scene_heading;
  const sceneHeading = sceneData?.scene?.scene_heading || listedSceneHeading || "";

  return (
    <ScriptContext.Provider
      value={{
        scene: sceneData?.scene as SceneData["scene"],
        scenes,
        scenesArePrevious,
        sceneId,
        setSceneId,
        documentId: documentId,
        tagDefinitions: sceneData?.tag_definitions as SceneData["tag_definitions"],
        tagCategories: sceneData?.tag_categories as SceneData["tag_categories"],
        notesList,
        setNotesList,
        selectedTagDef,
        setSelectedTagDef,
        hoveredTagDef,
        setHoveredTagDef,
        multiSelectedTagDef,
        setMultiSelectedTagDef,
        isSceneLoading,
        scenesLoaded,
        scenesLoadFailed,
        isFetchingScenes,
        selectedBlock,
        setSelectedBlock,
        doodFilteredStripboard,
        setDoodFilteredStripboard,
        sceneReport,
        setSceneReport,
        castMemberTagId,
        setCastMemberTagId,
        refetchScene,
        refetchScenes,
        sceneHeading,
      }}
    >
      {children}
    </ScriptContext.Provider>
  );
};

function groupTagsByMergedLabel(tag_definitions: GetSceneResponse["tag_definitions"]) {
  const organized: { [key: string]: { group_label: string; tags: TagDefinitionType[] }[] } = {};

  Object.keys(tag_definitions).forEach((category) => {
    const groups: { [key: string]: TagDefinitionType[] } = {};

    tag_definitions[category].forEach((tag) => {
      const rawMerged = tag.tag_merged_label?.trim() ?? "";
      const mapKey = rawMerged || tag.tag_display_name || tag.label;

      if (!groups[mapKey]) groups[mapKey] = [];

      groups[mapKey].push(tag);
    });

    organized[category] = Object.keys(groups)
      .map((mapKey) => ({
        group_label: mergedTagClusterDisplayLabel(
          groups[mapKey][0]?.tag_merged_label,
          groups[mapKey][0]?.tag_display_name || groups[mapKey][0]?.label || mapKey
        ),
        tags: groups[mapKey]
          .map((tag) => tag)
          .sort((a, b) => a.tag_definition_id - b.tag_definition_id),
      }))
      .sort((a, b) => a.tags[0].tag_definition_id - b.tags[0].tag_definition_id);
  });

  return organized;
}
